/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/naming-convention */
import useCloseReport from '@brands/Dashboard/ConsultationPage/hooks/useCloseReport';
import { isGumCase } from '@brands/Utils/isGumCase';
import { useLoadCustomerIoInApp } from '@brands/hooks/useLoadCustomerIoInApp';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { useSanitizeHtml } from '@brands/hooks/useSanitizeHtml';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { pdf } from '@react-pdf/renderer';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoClose, IoDownloadOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';

import DefaultLogo from '../../../../default/Dashboard/Appointments/assets/oHRLogo.svg';
import GumLogo from '../../../../gum-virtual-care/Dashboard/Appointments/assets/oHRLogo.svg';
import Button from '../../../Components/Button/Button';
import { Loading } from '../../../Components/LoadingSpinner/Loading';
import { getOneCase } from '../../../services/cases/getCaseById';
import { defaultICaseValues } from '../../../services/cases/types/defaultICaseValues';
import { ICase } from '../../../services/cases/types/ICase';
import { ImageWithNotes } from '../../../services/forms/types/ImageWithNotes';
import { getOneReportMedia } from '../../../services/oneReport/getOneReportMedia';
import { IOneReportMedia } from '../../../services/oneReport/types/IOneReportMedia';
import { displayErrorDetails } from '../../../Utils/displayError';
import OHRPdfDocument from '../Components/Documents/OHRPdfDocument/OHRPdfDocument';
import { convertSvgBase64ToPngBase64, svgToBase64 } from '../utils/consultationHelpers';
import printer from '../../Appointments/assets/printer.svg';
import {
  buildOHRReportViewModel,
  displayPhoneNumber,
  OHR_LEGAL_DISCLAIMER,
  OHRReportViewModel,
  ReportField,
  ReportRichText,
} from './reportViewModel';
import styles from './oralHealthReport.module.scss';

type ReportPageProps = {
  children: React.ReactNode;
  logo: string;
  organizationUsesCustomLogo?: boolean;
  pageNumber: number;
  totalPages: number;
  reportDate: string;
  gumLogo?: boolean;
};

const ReportPage = ({
  children,
  logo,
  organizationUsesCustomLogo,
  pageNumber,
  totalPages,
  reportDate,
  gumLogo,
}: ReportPageProps): JSX.Element => (
  <section className={styles.reportPage}>
    <header className={styles.reportHeader}>
      <div className={`${styles.reportLogoGroup} ${organizationUsesCustomLogo ? styles.poweredByTextParent : ''}`}>
        <img
          crossOrigin="anonymous"
          src={logo}
          alt="Logo"
          className={`${styles.reportLogo} ${gumLogo ? styles.gumLogoCorner : ''}`}
        />
        {organizationUsesCustomLogo && <span className={styles.poweredByText}>Powered by Dentistry.One</span>}
      </div>
      <div className={styles.reportHeaderMeta}>
        <span>Oral Health Report</span>
        <span>{reportDate}</span>
      </div>
    </header>
    <main className={styles.reportBody}>{children}</main>
    <footer className={styles.reportFooter}>
      Page {pageNumber} of {totalPages}
    </footer>
  </section>
);

type SectionProps = {
  title: string;
  children: React.ReactNode;
  className?: string;
};

const Section = ({ title, children, className = '' }: SectionProps): JSX.Element => (
  <section className={`${styles.reportSection} ${className}`}>
    <h2>{title}</h2>
    <div className={styles.reportDivider} />
    {children}
  </section>
);

const FieldList = ({ fields }: { fields: ReportField[] }): JSX.Element => (
  <div className={styles.fieldList}>
    {fields.map((field) => (
      <div key={`${field.label}-${field.value}`} className={styles.reportField}>
        <span className={styles.reportLabel}>{field.label}</span>
        <span className={`fs-exclude ${styles.reportValue}`}>{field.value}</span>
      </div>
    ))}
  </div>
);

const RichTextBlock = ({
  richText,
  sanitizeDisplayHtml,
  emptyText = '-',
}: {
  richText: ReportRichText | null;
  sanitizeDisplayHtml: (html: string) => string;
  emptyText?: string;
}): JSX.Element => (
  <div
    className={`fs-exclude ${styles.richText}`}
    dangerouslySetInnerHTML={{
      __html: richText ? sanitizeDisplayHtml(richText.html) : emptyText,
    }}
  />
);

const TwoColumnFields = ({
  left,
  right,
  third,
}: {
  left: ReportField[];
  right: ReportField[];
  third?: ReportField[];
}): JSX.Element => (
  <div className={`${styles.twoColumnGrid} ${third?.length ? styles.threeColumnGrid : ''}`}>
    <FieldList fields={left} />
    <FieldList fields={right} />
    {third?.length ? <FieldList fields={third} /> : null}
  </div>
);

const CdtCodes = ({ report }: { report: OHRReportViewModel }): JSX.Element => (
  <Section title="CDT codes" className={styles.tightSection}>
    {report.cdtCodes.length > 0 ? (
      <div className={styles.cdtList}>
        {report.cdtCodes.map((cdt) => (
          <p key={`${cdt.code}-${cdt.nomenclature}`} className="fs-exclude">
            <span>{cdt.code}: </span>
            <span>{cdt.nomenclature}</span>
          </p>
        ))}
      </div>
    ) : (
      <p className={`fs-exclude ${styles.reportValue}`}>-</p>
    )}
  </Section>
);

const OralHealthReportComponent = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const routeCaseId = useParams().caseId as string;
  const [thisCase, setCase] = useState<ICase>(defaultICaseValues);
  const currentLogo = isGumCase(thisCase) ? GumLogo : DefaultLogo;
  const componentRef = useRef<HTMLDivElement>(null);

  const [images, setImages] = useState<ImageWithNotes[]>([]);
  const [pdfLogo, setPdfLogo] = useState<string>();
  const [pharmacyAddress, setPharmacyAddress] = useState<string>('');
  const [pharmacyPhoneNumber, setPharmacyPhoneNumber] = useState<string>('');
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(null);
  const [medicalCondition, setMedicalCondition] = useState<MedicalConditions | null>(null);

  const { sanitizeDisplayHtml } = useSanitizeHtml();
  const { userInfo } = useAppSelector(selectAuth);
  useLoadCustomerIoInApp(userInfo?.role?.name === UserRoleName.Patient);

  const isPatientFacingReport =
    userInfo?.role?.name === UserRoleName.Patient || userInfo?.role?.name === UserRoleName.Dependent;

  const report = useMemo(
    () =>
      buildOHRReportViewModel({
        thisCase,
        images,
        medicalHistory,
        medicalCondition,
        pharmacyAddress,
        pharmacyPhoneNumber,
      }),
    [images, medicalCondition, medicalHistory, pharmacyAddress, pharmacyPhoneNumber, thisCase],
  );

  const totalPages = useMemo(
    () => 3 + (report.prescriptions.length > 0 ? 1 : 0) + (report.images.length > 0 ? 1 : 0),
    [report.images.length, report.prescriptions.length],
  );

  const goToPreviousPage = useCloseReport(thisCase.patient_id);

  const getCurrentCase = async (caseId: string): Promise<void> => {
    try {
      setLoading(true);
      const currentCase = await getOneCase(Number(caseId));
      setCase(currentCase);
    } catch (err) {
      displayErrorDetails(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCurrentCase(routeCaseId);
  }, [routeCaseId]);

  const loadImages = async (): Promise<void> => {
    if (!thisCase?.one_report?.media?.length) {
      setImages([]);
      return;
    }

    const imageResults = await Promise.all(
      thisCase.one_report.media.map(async (mediaOneReport: IOneReportMedia) => {
        try {
          const mediaData = await getOneReportMedia(mediaOneReport.media_id!);
          if (!mediaData?.url) return null;
          return {
            src: mediaData.url,
            width: 4,
            height: 3,
            notes: mediaOneReport.note,
            isSelected: mediaOneReport.is_selected,
            created_at: mediaOneReport.created_at,
          };
        } catch (error) {
          console.error(`Failed to load OHR media ${mediaOneReport.media_id}:`, error);
          return null;
        }
      }),
    );

    setImages(imageResults.filter((image): image is ImageWithNotes => image !== null));
  };

  const getFormAnswers = async (patientId: number): Promise<void> => {
    const { medical_history, medical_conditions } = await listClinicalData(patientId);

    setMedicalHistory(medical_history);
    setMedicalCondition(medical_conditions);

    setPharmacyAddress(
      [
        medical_history?.pharmacy_street_address,
        medical_history?.pharmacy_city,
        medical_history?.pharmacy_state,
        medical_history?.pharmacy_zip_code,
      ]
        .filter(Boolean)
        .join(', '),
    );
    setPharmacyPhoneNumber(displayPhoneNumber(medical_history?.pharmacy_phone_number || ''));
  };

  const downloadOHRPdf = async (): Promise<void> => {
    const newTab = window.open('', '_blank');

    try {
      setLoading(true);
      const updatedCase = await getOneCase(Number(routeCaseId));

      const blob = await pdf(
        <OHRPdfDocument
          thisCase={updatedCase}
          logoBase64={pdfLogo || ''}
          images={images}
          medicalHistory={medicalHistory}
          medicalCondition={medicalCondition}
          pharmacyAddress={pharmacyAddress}
          pharmacyPhoneNumber={pharmacyPhoneNumber}
          hideAttestationChecklistCopy={isPatientFacingReport}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);

      if (newTab) {
        newTab.location.href = url;
      } else {
        window.open(url, '_blank');
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = 'OralHealthReport.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      displayErrorDetails(err);
      newTab?.close();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!thisCase?.organization?.id) return;
    const logoPath = isGumCase(thisCase) ? GumLogo : DefaultLogo;
    svgToBase64(logoPath)
      .then((base64Logo) => convertSvgBase64ToPngBase64(base64Logo))
      .then(setPdfLogo)
      .catch(console.error);
  }, [thisCase?.organization?.id, thisCase?.client?.code]);

  useEffect(() => {
    loadImages();
    if (thisCase?.medical_history_snapshot) {
      const medical_history = thisCase.medical_history_snapshot;

      setMedicalHistory(medical_history);
      setMedicalCondition(thisCase.medical_conditions_snapshot);

      setPharmacyAddress(
        [
          medical_history?.pharmacy_street_address,
          medical_history?.pharmacy_city,
          medical_history?.pharmacy_state,
          medical_history?.pharmacy_zip_code,
        ]
          .filter(Boolean)
          .join(', '),
      );
      setPharmacyPhoneNumber(displayPhoneNumber(medical_history?.pharmacy_phone_number || ''));
    } else if (thisCase?.patient?.id) {
      getFormAnswers(Number(thisCase.patient.id));
    }
  }, [thisCase]);

  if (!thisCase || loading) return <Loading fullScreen />;

  let pageNumber = 1;
  const pageProps = {
    logo: currentLogo,
    organizationUsesCustomLogo: thisCase.organization.uses_custom_logo,
    reportDate: report.headerDate,
    totalPages,
    gumLogo: isGumCase(thisCase),
  };

  return (
    <div className={styles.modalBackground}>
      <div>
        <div className={`fs-unmask ${styles.modalCloseBtn}`} onClick={goToPreviousPage}>
          <span>Close</span>
          <div className={styles.circle}>
            <IoClose />
          </div>
        </div>
        <ReactToPrint
          trigger={() => (
            <Button type="button" className={styles.print}>
              <img src={printer} alt="Print" />
              Print
            </Button>
          )}
          content={() => componentRef.current}
        />
        <div className={styles.download}>
          <Button type="button" className={styles.print} onClick={downloadOHRPdf}>
            <IoDownloadOutline />
            Download
          </Button>
        </div>
      </div>
      <div className={styles.modalDiv}>
        <div className={styles.modalContainer}>
          <div className={styles.printableDiv} ref={componentRef}>
            <ReportPage {...pageProps} pageNumber={pageNumber++}>
              <Section title="Appointment details">
                <TwoColumnFields left={report.appointmentLeft} right={report.appointmentRight} />
              </Section>
              <Section title="Care team">
                <TwoColumnFields
                  left={report.careTeamLeft}
                  right={report.careTeamRight}
                  third={report.careTeamCollector}
                />
              </Section>
              <Section title="Patient details">
                <TwoColumnFields left={report.patientLeft} right={report.patientRight} />
              </Section>
            </ReportPage>

            <ReportPage {...pageProps} pageNumber={pageNumber++}>
              <Section title="Visit summary">
                <div className={styles.twoColumnGrid}>
                  <FieldList fields={report.visitSummaryLeft} />
                  <div className={styles.visitCommentColumn}>
                    <div className={styles.reportField}>
                      <span className={styles.reportLabel}>Other comments to provider</span>
                      {report.additionalInfo.length > 0 ? (
                        report.additionalInfo.map((info) => (
                          <div key={info.id} className={styles.additionalInfo}>
                            {info.date && <span className={styles.infoDate}>{info.date}</span>}
                            <span className={`fs-exclude ${styles.reportValue}`}>{info.text}</span>
                          </div>
                        ))
                      ) : (
                        <span className={`fs-exclude ${styles.reportValue}`}>-</span>
                      )}
                    </div>
                    {report.providerComment && (
                      <div className={styles.reportField}>
                        <span className={styles.reportLabel}>Additional comments from provider</span>
                        <RichTextBlock richText={report.providerComment} sanitizeDisplayHtml={sanitizeDisplayHtml} />
                      </div>
                    )}
                  </div>
                </div>
              </Section>
              {report.assessment && (
                <Section title="Assessment" className={styles.tightSection}>
                  <RichTextBlock richText={report.assessment} sanitizeDisplayHtml={sanitizeDisplayHtml} />
                </Section>
              )}
              <CdtCodes report={report} />
            </ReportPage>

            {report.prescriptions.length > 0 && (
              <ReportPage {...pageProps} pageNumber={pageNumber++}>
                <Section title="Prescriptions">
                  {report.prescriptions.map((prescription) => (
                    <div key={prescription.id} className={styles.prescriptionBlock}>
                      <FieldList
                        fields={[
                          { label: 'Drug name', value: prescription.drug },
                          { label: 'Instructions', value: prescription.instructions },
                          { label: 'Prescription date', value: prescription.prescriptionDate },
                          { label: 'Dispense', value: prescription.dispense },
                          { label: 'Refills', value: prescription.refills },
                          ...prescription.pharmacy,
                        ].filter((field) => field.value)}
                      />
                    </div>
                  ))}
                </Section>
              </ReportPage>
            )}

            {report.images.length > 0 && (
              <ReportPage {...pageProps} pageNumber={pageNumber++}>
                <Section title="Images">
                  <div className={styles.imageGrid}>
                    {report.images.map((image) => (
                      <div key={image.id} className={styles.imageCard}>
                        <img crossOrigin="anonymous" alt="Patient attachment" src={image.src} />
                        {image.notes && <p className={`fs-exclude ${styles.imageCaption}`}>{image.notes}</p>}
                      </div>
                    ))}
                  </div>
                </Section>
              </ReportPage>
            )}

            <ReportPage {...pageProps} pageNumber={pageNumber++}>
              <Section title="Recommended treatment">
                <div className={styles.reportField}>
                  <span className={styles.reportLabel}>Description</span>
                  <RichTextBlock richText={report.recommendedTreatment} sanitizeDisplayHtml={sanitizeDisplayHtml} />
                </div>
              </Section>

              {report.treatments.length > 0 && (
                <Section title="Treatment">
                  <div className={styles.treatmentList}>
                    {report.treatments.map((treatment) => (
                      <div key={treatment.id} className={styles.treatmentItem}>
                        <FieldList fields={treatment.fields} />
                        {treatment.description && (
                          <div className={styles.reportField}>
                            <span className={styles.reportLabel}>Description</span>
                            <span className={`fs-exclude ${styles.reportValue}`}>{treatment.description}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {report.followUpColumns.length > 0 && (
                <Section title="Recommended follow-up care">
                  <div className={styles.twoColumnGrid}>
                    {report.followUpColumns.map((followUp) => (
                      <div key={followUp.id} className={styles.fieldList}>
                        <div className={styles.reportField}>
                          <span className={styles.reportLabel}>{followUp.recommendedLabel}</span>
                          <span className={`fs-exclude ${styles.reportValue}`}>{followUp.recommendedValue}</span>
                        </div>
                        <FieldList fields={followUp.fields} />
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {report.fulfillmentApproval && (
                <Section title="Fulfillment approval">
                  <div className={styles.fieldList}>
                    <div className={styles.reportField}>
                      <span className={styles.reportLabel}>Decision</span>
                      <span className={`fs-exclude ${styles.reportValue}`}>{report.fulfillmentApproval.decision}</span>
                    </div>
                    {report.fulfillmentApproval.notes && (
                      <div className={styles.reportField}>
                        <span className={styles.reportLabel}>Notes</span>
                        <span className={`fs-exclude ${styles.reportValue}`}>{report.fulfillmentApproval.notes}</span>
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {report.educations.length > 0 && (
                <Section title="Educational resources">
                  <div className={styles.educationList}>
                    {report.educations.map((education) => (
                      <div key={education.id} className={styles.educationItem}>
                        <span className={`fs-exclude ${styles.reportValue}`}>{education.title}</span>
                        <a className="fs-exclude" href={education.url}>
                          {education.url}
                        </a>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {report.notes.length > 0 && (
                <Section title="Notes">
                  <div className={styles.notesList}>
                    {report.notes.map((note) => (
                      <div key={note.id} className={styles.noteItem}>
                        <span className={styles.noteMeta}>{note.meta}</span>
                        <div
                          className={`fs-exclude ${styles.noteText}`}
                          dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(note.html) }}
                        />
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {report.providerAttestation && (
                <Section title="Provider attestation">
                  <div className={`${styles.noteItem} no-page-break`}>
                    <span className={styles.noteMeta}>
                      {report.providerAttestation.date} | {report.providerAttestation.providerName} (provider)
                    </span>
                    {!isPatientFacingReport && (
                      <p className={`fs-unmask ${styles.noteText}`}>Compliance checklist confirmed</p>
                    )}
                  </div>
                </Section>
              )}

              <div className={styles.disclaimerBlock}>
                <span>DISCLAIMER</span>
                <p className="fs-unmask">{OHR_LEGAL_DISCLAIMER}</p>
              </div>
            </ReportPage>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OralHealthReportComponent;
